import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Users, FileText, History, Paperclip, Loader2, Save, Trash2, Copy, Eye, X, RefreshCw, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GROUPS = [
  { key: 'families',      label: 'Families',          hasNukh: true  },
  { key: 'ps',            label: 'PS 2026',           hasNukh: false },
  { key: 'professionals', label: 'Professionals',     hasNukh: true  },
  { key: 'donors',        label: 'Scholarship Donors',hasNukh: false },
  { key: 'vision',        label: 'Vision Sangam',     hasNukh: false },
];

const DEFAULT_TARGETING = () => ({
  families:      { enabled: false, status: 'approved', city: '', nukh: '' },
  ps:            { enabled: false, status: 'approved', city: '' },
  professionals: { enabled: false, status: 'approved', city: '', nukh: '' },
  donors:        { enabled: false, status: 'approved', city: '' },
  vision:        { enabled: false, status: 'approved', city: '' },
  customEmails:  [],
});

const EmailCampaigns = () => {
  const [tab, setTab] = useState('compose'); // compose | templates | history
  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // ------- Compose state -------
  const [campaignId, setCampaignId] = useState(null); // null until first draft save
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [targeting, setTargeting] = useState(DEFAULT_TARGETING());
  const [customEmailsRaw, setCustomEmailsRaw] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);

  // ------- Preview / send -------
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, sent: 0, failed: 0 });

  // ------- Test send -------
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // ------- Templates -------
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');

  // ------- History -------
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchHistory();
  }, []);

  // Sync customEmailsRaw ↔ targeting.customEmails
  useEffect(() => {
    const list = customEmailsRaw
      .split(/[\s,;\n]+/)
      .map(s => s.trim())
      .filter(Boolean);
    setTargeting(prev => ({ ...prev, customEmails: list }));
  }, [customEmailsRaw]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-templates`, authHeader);
      setTemplates(res.data.templates || []);
    } catch (e) {
      // silent
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-campaigns`, authHeader);
      setHistory(res.data.campaigns || []);
    } catch (e) {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  const updateGroup = (key, patch) => {
    setTargeting(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const resetCompose = () => {
    setCampaignId(null);
    setSubject('');
    setHtmlBody('');
    setTargeting(DEFAULT_TARGETING());
    setCustomEmailsRaw('');
    setAttachments([]);
    setPreview(null);
  };

  // ------- Draft save (creates or updates campaign) -------
  const ensureDraft = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required');
      return null;
    }
    if (!htmlBody || !htmlBody.replace(/<[^>]*>/g, '').trim()) {
      toast.error('Body is required');
      return null;
    }
    setSavingDraft(true);
    try {
      if (!campaignId) {
        const res = await axios.post(`${API_URL}/api/admin/email-campaigns`, {
          subject, htmlBody, targeting,
        }, authHeader);
        setCampaignId(res.data.id);
        toast.success('Draft saved');
        fetchHistory();
        return res.data.id;
      } else {
        await axios.patch(`${API_URL}/api/admin/email-campaigns/${campaignId}`, {
          subject, htmlBody, targeting,
        }, authHeader);
        toast.success('Draft updated');
        fetchHistory();
        return campaignId;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save draft');
      return null;
    } finally {
      setSavingDraft(false);
    }
  };

  // ------- Attachments -------
  const uploadAttachment = async (file) => {
    if (!file) return;
    const id = await ensureDraft();
    if (!id) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-campaigns/${id}/attachments`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setAttachments(prev => [...prev, res.data]);
      toast.success('Attachment added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const removeAttachment = async (attId) => {
    if (!campaignId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-campaigns/${campaignId}/attachments/${attId}`, authHeader);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  // ------- Preview recipients -------
  const doPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-campaigns/preview`, { targeting }, authHeader);
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  // ------- Test send -------
  const doTestSend = async () => {
    const id = await ensureDraft();
    if (!id) return;
    if (!testEmail.trim()) {
      toast.error('Enter a test email address');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/api/admin/email-campaigns/${id}/test`, { email: testEmail.trim() }, authHeader);
      toast.success(`Test email sent to ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  // ------- Send campaign -------
  const openSendModal = async () => {
    const id = await ensureDraft();
    if (!id) return;
    await doPreview();
    setShowSendModal(true);
  };

  const confirmSend = async () => {
    if (!campaignId) return;
    setSending(true);
    try {
      const startRes = await axios.post(`${API_URL}/api/admin/email-campaigns/${campaignId}/start`, {}, authHeader);
      const total = startRes.data.total;
      const batchSize = startRes.data.batchSize || 25;
      const batches = Math.ceil(total / batchSize);
      let sent = 0, failed = 0;
      for (let i = 0; i < batches; i++) {
        const skip = i * batchSize;
        setProgress({ current: i + 1, total: batches, sent, failed });
        const r = await axios.post(
          `${API_URL}/api/admin/email-campaigns/${campaignId}/send?skip=${skip}&limit=${batchSize}`,
          {}, authHeader
        );
        sent += r.data.sent || 0;
        failed += r.data.failed || 0;
        setProgress({ current: i + 1, total: batches, sent, failed });
      }
      await axios.post(`${API_URL}/api/admin/email-campaigns/${campaignId}/finalize`, {}, authHeader);
      toast.success(`Campaign sent — ${sent} delivered${failed ? `, ${failed} failed` : ''}`);
      fetchHistory();
      setShowSendModal(false);
      resetCompose();
      setTab('history');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // ------- Templates -------
  const saveAsTemplate = async () => {
    if (!templateName.trim()) return toast.error('Template name is required');
    if (!subject.trim() || !htmlBody.trim()) return toast.error('Subject and body needed');
    try {
      await axios.post(`${API_URL}/api/admin/email-templates`, {
        name: templateName.trim(), subject, htmlBody,
      }, authHeader);
      toast.success('Template saved');
      setTemplateName('');
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const applyTemplate = (t) => {
    setSubject(t.subject || '');
    setHtmlBody(t.htmlBody || '');
    setTab('compose');
    toast.success(`Template "${t.name}" loaded`);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-templates/${id}`, authHeader);
      fetchTemplates();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ------- History -------
  const openCampaign = async (c) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-campaigns/${c.id}`, authHeader);
      const d = res.data;
      setCampaignId(d.status === 'draft' ? d.id : null); // only drafts remain editable
      setSubject(d.subject || '');
      setHtmlBody(d.htmlBody || '');
      setTargeting({ ...DEFAULT_TARGETING(), ...(d.targeting || {}) });
      setCustomEmailsRaw(((d.targeting || {}).customEmails || []).join(', '));
      setAttachments(d.attachments || []);
      setTab('compose');
    } catch (e) {
      toast.error('Failed to load campaign');
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-campaigns/${id}`, authHeader);
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ============ RENDER ============
  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition ${
        tab === id ? 'bg-primary text-white shadow' : 'text-gray-600 hover:bg-gray-100'
      }`}
      data-testid={`email-tab-${id}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Send Email</h1>
          <p className="text-sm text-gray-500">Compose, target and broadcast emails to members</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <TabBtn id="compose" icon={Mail} label="Compose" />
        <TabBtn id="templates" icon={FileText} label={`Templates (${templates.length})`} />
        <TabBtn id="history" icon={History} label={`History (${history.length})`} />
      </div>

      {/* COMPOSE TAB */}
      {tab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Editor column */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-4">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="mt-1"
                data-testid="email-subject"
              />
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Body</label>
                <span className="text-xs text-gray-400">Rich text · HTML output</span>
              </div>
              <RichTextEditor value={htmlBody} onChange={setHtmlBody} />
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Attachments
                </label>
                <label className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add file
                  <input type="file" className="hidden" onChange={(e) => { uploadAttachment(e.target.files?.[0]); e.target.value = ''; }} data-testid="email-attachment-input" />
                </label>
              </div>
              {attachments.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No attachments</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {attachments.map(a => (
                    <li key={a.id} className="flex items-center justify-between border rounded-md px-3 py-1.5 bg-gray-50">
                      <span className="truncate flex-1 mr-2">{a.filename} <span className="text-xs text-gray-400">({Math.round((a.size||0)/1024)} KB)</span></span>
                      <button onClick={() => removeAttachment(a.id)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-4">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Save as reusable template</label>
              <div className="flex gap-2 mt-1">
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" data-testid="email-template-name" />
                <Button variant="outline" onClick={saveAsTemplate}><Save className="w-4 h-4 mr-1" /> Save Template</Button>
              </div>
            </Card>
          </div>

          {/* Targeting column */}
          <div className="space-y-4">
            <Card className="p-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-800 mb-3">
                <Users className="w-4 h-4" /> Recipients
              </h3>
              <div className="space-y-3">
                {GROUPS.map(g => (
                  <div key={g.key} className="border rounded-lg p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targeting[g.key].enabled}
                        onChange={(e) => updateGroup(g.key, { enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid={`email-group-${g.key}`}
                      />
                      <span className="font-medium text-sm">{g.label}</span>
                    </label>
                    {targeting[g.key].enabled && (
                      <div className="grid grid-cols-1 gap-2 mt-3 pl-6">
                        <select
                          value={targeting[g.key].status || 'any'}
                          onChange={(e) => updateGroup(g.key, { status: e.target.value })}
                          className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                          data-testid={`email-group-${g.key}-status`}
                        >
                          <option value="any">Any status</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        <Input
                          value={targeting[g.key].city || ''}
                          onChange={(e) => updateGroup(g.key, { city: e.target.value })}
                          placeholder="Filter by city (optional)"
                          className="h-8 text-xs"
                          data-testid={`email-group-${g.key}-city`}
                        />
                        {g.hasNukh && (
                          <Input
                            value={targeting[g.key].nukh || ''}
                            onChange={(e) => updateGroup(g.key, { nukh: e.target.value })}
                            placeholder="Filter by nukh (optional)"
                            className="h-8 text-xs"
                            data-testid={`email-group-${g.key}-nukh`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <div className="border rounded-lg p-3">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Custom email addresses</label>
                  <textarea
                    value={customEmailsRaw}
                    onChange={(e) => setCustomEmailsRaw(e.target.value)}
                    placeholder="Comma or newline separated"
                    className="mt-1 w-full min-h-[60px] px-2 py-1.5 rounded-md border border-input bg-background text-xs"
                    data-testid="email-custom-list"
                  />
                </div>
              </div>

              <Button variant="outline" className="w-full mt-3" onClick={doPreview} disabled={loadingPreview} data-testid="email-preview-btn">
                {loadingPreview ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                Preview Recipients
              </Button>
              {preview && (
                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm">
                  <div className="text-2xl font-bold text-blue-900">{preview.total}</div>
                  <div className="text-xs text-blue-700">unique recipients</div>
                  <div className="mt-2 space-y-0.5 text-xs text-blue-800">
                    {Object.entries(preview.breakdown || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span>{v}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-800 mb-3">
                <Send className="w-4 h-4" /> Actions
              </h3>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="h-9 text-sm"
                    data-testid="email-test-address"
                  />
                  <Button variant="outline" size="sm" onClick={doTestSend} disabled={sendingTest} data-testid="email-test-btn">
                    {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
                  </Button>
                </div>
                <Button variant="outline" className="w-full" onClick={() => ensureDraft()} disabled={savingDraft} data-testid="email-save-draft">
                  {savingDraft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Draft
                </Button>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={openSendModal} data-testid="email-send-btn">
                  <Send className="w-4 h-4 mr-1" /> Send Campaign
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* TEMPLATES TAB */}
      {tab === 'templates' && (
        <Card className="p-4">
          {templates.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No templates yet. Save one from the Compose tab.</p>
          ) : (
            <ul className="divide-y">
              {templates.map(t => (
                <li key={t.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">{t.name}</div>
                    <div className="text-sm text-gray-600 truncate">{t.subject || <em className="text-gray-400">no subject</em>}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Updated {new Date(t.updatedAt).toLocaleString('en-IN')}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => applyTemplate(t)}><Copy className="w-4 h-4 mr-1" />Use</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteTemplate(t.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <Card className="p-4">
          <div className="flex justify-between mb-3">
            <span className="text-sm text-gray-500">Recent campaigns</span>
            <Button size="sm" variant="outline" onClick={fetchHistory}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          </div>
          {loadingHistory ? (
            <p className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></p>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No campaigns yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-2">Subject</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Recipients</th>
                    <th className="py-2 px-2">Sent</th>
                    <th className="py-2 px-2">Failed</th>
                    <th className="py-2 px-2">Created</th>
                    <th className="py-2 pl-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(c => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-2 max-w-[280px] truncate">{c.subject}</td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'sent' ? 'bg-green-100 text-green-700' :
                          c.status === 'sending' ? 'bg-blue-100 text-blue-700' :
                          c.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                          'bg-red-100 text-red-700'
                        }`}>{c.status}</span>
                      </td>
                      <td className="py-2 px-2">{c.recipientCount || 0}</td>
                      <td className="py-2 px-2 text-green-700">{c.sentCount || 0}</td>
                      <td className="py-2 px-2 text-red-600">{c.failedCount || 0}</td>
                      <td className="py-2 px-2 text-xs text-gray-500">{c.createdAt ? new Date(c.createdAt).toLocaleString('en-IN') : '—'}</td>
                      <td className="py-2 pl-2 flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openCampaign(c)} title="Open"><Eye className="w-4 h-4" /></Button>
                        {c.status !== 'sending' && (
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCampaign(c.id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* SEND CONFIRMATION MODAL */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !sending && setShowSendModal(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="email-send-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Send Campaign</h3>
                <p className="text-xs text-gray-500 mt-1 truncate max-w-[380px]">Subject: {subject}</p>
              </div>
              {!sending && <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>}
            </div>

            {preview && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 mb-4">
                <div className="text-3xl font-bold text-blue-800">{preview.total}</div>
                <div className="text-xs text-blue-700 uppercase tracking-wide mt-0.5">unique recipients</div>
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-blue-900">
                  {Object.entries(preview.breakdown || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-blue-100 py-0.5"><span className="capitalize">{k}</span><span>{v}</span></div>
                  ))}
                </div>
              </div>
            )}

            {sending && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Batch {progress.current} / {progress.total}</span>
                  <span>{progress.sent} sent · {progress.failed} failed</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }} />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSendModal(false)} disabled={sending}>Cancel</Button>
              <Button onClick={confirmSend} disabled={sending || !preview?.total} className="bg-blue-600 hover:bg-blue-700" data-testid="email-confirm-send">
                {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Send Now</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;
